import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import LogicOptions from '../LogicOptions';
import QuestionLogicOptions from '../QuestionLogicOptions';

import {
  answerOperators,
  availableLogicOptions,
  instantSurveyGroupRefUrlVariable,
  LOGIC_OPTIONS,
} from '../../utilities/constants';
import { SurveyContext } from '../../../../../../utilities/context';

import styles from './_index.module.scss';

const DisqualifyLogicRow = ({
  firstErrorRef,
  index,
  isCompleted,
  question,
  questionId,
  questionLogic,
  setQuestionLogic,
}) => {
  const { survey } = useContext(SurveyContext);

  const getLogicType = () =>
    questionLogic[questionId].disqualify?.logic[index]?.url_variable ===
    instantSurveyGroupRefUrlVariable
      ? LOGIC_OPTIONS.QUOTA_GROUP.value
      : LOGIC_OPTIONS.ANSWER.value;

  const [logicType, setLogicType] = useState(getLogicType);

  useEffect(() => {
    setLogicType(getLogicType());
  }, [index, questionId, questionLogic]);

  const quotaGroupOptions = survey.groups
    .filter(({ id }) => id !== null && id !== undefined)
    .map((group) => ({
      id: group.id,
      text: group.name,
    }));

  const isQuotaGroupSelectionValid = () => {
    const groupIds = quotaGroupOptions.map(({ id }) => String(id));
    const logicQuotaGroupIds = (
      questionLogic[questionId]?.disqualify?.logic[index]?.values || []
    ).map(String);
    return logicQuotaGroupIds.every((id) => groupIds.includes(id));
  };

  const updateAnswers = (answers) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[questionId].disqualify.logic[index].answers = answers;
    updatedLogic[questionId].disqualify.logic[index].hasAnswerError = false;
    updatedLogic[questionId].disqualify.logic[index].isFirstAnswerError = false;
    setQuestionLogic(updatedLogic);
  };

  const updateLogicType = (newLogicType) => {
    setLogicType(newLogicType);
    const updatedLogic = cloneDeep(questionLogic);
    if (newLogicType === LOGIC_OPTIONS.QUOTA_GROUP.value) {
      updatedLogic[questionId].disqualify.logic[index] = {
        hasAnswerError: false,
        operator: answerOperators[0],
        url_variable: instantSurveyGroupRefUrlVariable,
        values: [],
      };
    } else {
      updatedLogic[questionId].disqualify.logic[index] = {
        answers: [],
        hasAnswerError: false,
        isFirstAnswerError: false,
        operator: answerOperators[0],
      };
    }
    setQuestionLogic(updatedLogic);
  };

  const updateOperator = (operator) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[questionId].disqualify.logic[index].operator = operator;
    setQuestionLogic(updatedLogic);
  };

  const updateQuotaGroups = (groups) => {
    const updatedLogic = cloneDeep(questionLogic);
    const groupIds = groups.map(({ id }) => String(id));
    updatedLogic[questionId].disqualify.logic[index].values = groupIds;
    updatedLogic[questionId].disqualify.logic[index].hasAnswerError = false;
    setQuestionLogic(updatedLogic);
  };

  return (
    <div className={styles['disqualify-logic-row']}>
      <span>
        The{' '}
        <LogicOptions
          isCompleted={isCompleted}
          logicOptions={availableLogicOptions}
          optionsLabel="Type"
          setValue={updateLogicType}
          value={logicType}
        />
      </span>
      {logicType === LOGIC_OPTIONS.ANSWER.value ? (
        <>
          <span>&nbsp;is&nbsp;</span>
          <QuestionLogicOptions
            isCompleted={isCompleted}
            options={answerOperators}
            optionsLabel="Operator"
            optionsType="radio"
            setValue={updateOperator}
            value={questionLogic[questionId].disqualify.logic[index].operator}
          />
          <span>&nbsp;:&nbsp;</span>
          <QuestionLogicOptions
            firstErrorRef={firstErrorRef}
            isCompleted={isCompleted}
            isError={questionLogic[questionId].disqualify.logic[index].hasAnswerError}
            isFocused={questionLogic[questionId].disqualify.logic[index].isFirstAnswerError}
            isRequired
            options={question.answers}
            optionsLabel="Answer Options"
            optionsType="checkbox"
            setValue={updateAnswers}
            value={questionLogic[questionId].disqualify.logic[index].answers ?? []}
          />
        </>
      ) : (
        <>
          <span>&nbsp;is&nbsp;</span>
          <QuestionLogicOptions
            isCompleted={isCompleted}
            options={answerOperators}
            optionsLabel="Operator"
            optionsType="radio"
            setValue={updateOperator}
            value={questionLogic[questionId].disqualify.logic[index].operator}
          />
          <span>&nbsp;:&nbsp;</span>
          <QuestionLogicOptions
            firstErrorRef={firstErrorRef}
            isCompleted={isCompleted}
            isError={
              !isQuotaGroupSelectionValid() ||
              questionLogic[questionId].disqualify.logic[index].hasAnswerError
            }
            isFocused={questionLogic[questionId].disqualify.logic[index].hasAnswerError}
            isQuotaGroup
            isQuotaGroupSelectionValid={isQuotaGroupSelectionValid()}
            isRequired
            options={quotaGroupOptions}
            optionsLabel="Quota Group"
            optionsType="checkbox"
            setValue={updateQuotaGroups}
            value={quotaGroupOptions.filter(({ id }) =>
              (questionLogic[questionId].disqualify.logic[index].values || []).includes(String(id))
            )}
          />
        </>
      )}
    </div>
  );
};

DisqualifyLogicRow.propTypes = {
  firstErrorRef: PropTypes.object,
  index: PropTypes.number.isRequired,
  isCompleted: PropTypes.bool,
  question: PropTypes.object.isRequired,
  questionId: PropTypes.number.isRequired,
  questionLogic: PropTypes.object.isRequired,
  setQuestionLogic: PropTypes.func,
};

export default DisqualifyLogicRow;
